import dayjs from "dayjs";
import { get } from "lodash";
import moment from "moment";
import {
  AUTHORITY_TYPE,
  CONTACT_POINT_SYSTEM,
  MAX_FEES_VALUE,
  NIR_INHERITANCE_FROM,
  REGEX_EXPS,
  SALE_MODES,
} from "utils/constants";
import { isMajor } from "utils/helpers/date";
import {
  childUsesStrictNirValidation,
  childHasSocialRegimeAttachmentMismatch,
  getAgeFromBirthDate,
  isChildLinkedToAdherent,
  isChildLinkedToSpouse,
} from "utils/helpers/miltisRelatedPersonChild";
import {
  createContractNumberValidation,
  normalizeTextField,
  stripAllSpaces,
} from "utils/helpers/validations";
import {
  getPhonePrefixFromYupContext,
  isValidLocalPhoneNumber,
} from "utils/helpers/phone";
import {
  softValidateNIR,
  validateBIC,
  validateIBAN,
  validateNIR,
} from "utils/helpers/values";
import * as yup from "yup";

const validation = (messages, { hasPartner, checkUser, isLumineis }) => {
  const string = yup.string().typeError(messages.NOT_STRING).nullable();
  const textField = yup
    .string()
    .transform(normalizeTextField)
    .typeError(messages.NOT_STRING)
    .nullable();
  const noSpaceString = yup.string().transform(stripAllSpaces);
  const requiredTextField = textField
    .clone()
    .typeError(messages.REQUIRED)
    .required(messages.REQUIRED);
  const nameValidation = textField
    .notRequired()
    .test(
      "is-valid-name",
      messages.INVALID_NAME,
      (value) => !value || REGEX_EXPS.name.test(value),
    );

  const relatedPersonValidationSchema = ({ birthDate }) =>
    yup.object().shape({
      identity: yup.object().shape({
        user: yup.object().shape({
          lastname: nameValidation,
          firstname: nameValidation,
          birthDate,
        }),
        socioProfessionalCategory: string,
        socialRegime: string.test(
          "spouse-regime-children",
          messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
          function (value) {
            const root = this.from[this.from.length - 1].value;
            const children = get(root, "children", []);
            const hasSpouse = Boolean(get(root, "spouse"));
            const hasMismatch = children.some(
              (child) =>
                isChildLinkedToSpouse({
                  authority: get(child, "authority"),
                  nirInheritanceFrom: get(
                    child,
                    "identity.fields.nirInheritanceFrom",
                  ),
                  hasSpouse,
                }) &&
                childHasSocialRegimeAttachmentMismatch({
                  child,
                  adherentSocialRegime: get(
                    root,
                    "project.contact.socialRegime",
                  ),
                  spouseSocialRegime: value,
                  hasSpouse,
                }),
            );
            if (!hasMismatch) return true;
            return this.createError({
              message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
            });
          },
        ),
        socialSecurityNumber: noSpaceString
          .typeError(messages.REQUIRED)
          .nullable()
          .optional()
          .test(
            "securityNumberFormat",
            messages.INVALID_VALUE,
            function (value, ctx) {
              if (!value) return true;
              const result = validateNIR(value, this.parent);
              if (!result.valid)
                return ctx.createError({
                  message: messages[result.reason] || messages.INVALID_VALUE,
                });

              return true;
            },
          ),
      }),
    });
  return yup.object().shape({
    createdDate: yup.date(),
    isPayerReceiver: yup.string().typeError(messages.NOT_STRING),
    additionalInfo: yup
      .object()
      .shape({
        brokerageFees: yup
          .number()
          .max(MAX_FEES_VALUE, `${messages.MAX} ${MAX_FEES_VALUE}`)
          .min(0, `${messages.MIN} 0`)
          .nullable()
          .optional(),
      })
      .optional(),
    project: yup.object().shape({
      projectType: yup.mixed().when("contract.saleMode", {
        is: (saleMode) => saleMode === SALE_MODES.DISTANT,
        then: string,
      }),
      contract: yup.object().shape({
        issuanceDate: yup.date(),
        caseType: string,
        saleMode: string,
      }),
      fields: yup.object().shape({
        selfOccupation: yup.boolean(),
        contractNumber: string,
        dueDate: yup.date(),
        recipient: string,
        reason: string,
        acceptProductStatuses: yup.boolean(),
        acceptAgreement: yup.boolean(),
        acceptFees: yup.boolean(),
        phoneCanvassing: yup.boolean(),
        phoneCanvassingConsent: yup.boolean().when("phoneCanvassing", {
          is: true,
          then: yup
            .boolean()
            .oneOf([true], messages.REQUIRED)
            .required(messages.REQUIRED),
        }),
      }),
      terminationRequest: yup
        .mixed()
        .when(
          ["fields.selfOccupation", "ria", "fields.contractMoreThanOneYear"],
          {
            is: (selfOccupation, ria, contractMoreThanOneYear) =>
              ria === true &&
              contractMoreThanOneYear === true &&
              selfOccupation === false,
            then: () =>
              yup.object().shape(
                isLumineis
                  ? {
                      contractNumber: createContractNumberValidation(
                        messages,
                        REGEX_EXPS,
                      ),
                      address: requiredTextField,
                      zipCode: requiredTextField.matches(
                        REGEX_EXPS.zipCode,
                        messages.INVALID_VALUE,
                      ),
                      city: requiredTextField,
                      email: yup
                        .string()
                        .typeError(messages.REQUIRED)
                        .email(messages.EMAIL)
                        .required(messages.REQUIRED),
                      brokerageMandate: yup.object().shape({
                        currentCompany: requiredTextField,
                      }),
                      reason: string,
                    }
                  : {
                      contractNumber: createContractNumberValidation(
                        messages,
                        REGEX_EXPS,
                      ),
                      dueDate: yup
                        .date()
                        .typeError(messages.REQUIRED)
                        .required(messages.REQUIRED)
                        .test(
                          "dueDateFormat",
                          messages.INVALID_DUE_DATE,
                          (value) => {
                            return (
                              dayjs(value).isValid() &&
                              dayjs(value).isBefore(dayjs().subtract(1, "year"))
                            );
                          },
                        ),
                      recipient: requiredTextField,
                      reason: string,
                    },
              ),
            otherwise: () => yup.object().nullable().optional(),
          },
        ),
      contact: yup.object().shape({
        user: yup.object().shape({
          gender: string,
          lastname: nameValidation,
          firstname: nameValidation,
          birthName: nameValidation,
          birthDate: yup.date(),
        }),
        socialRegime: string.test(
          "adherent-regime-children",
          messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
          function (value) {
            const root = this.from[this.from.length - 1].value;
            const children = get(root, "children", []);
            const hasSpouse = Boolean(get(root, "spouse"));
            const hasMismatch = children.some(
              (child) =>
                isChildLinkedToAdherent({
                  authority: get(child, "authority"),
                  nirInheritanceFrom: get(
                    child,
                    "identity.fields.nirInheritanceFrom",
                  ),
                }) &&
                childHasSocialRegimeAttachmentMismatch({
                  child,
                  adherentSocialRegime: value,
                  spouseSocialRegime: get(root, "spouse.identity.socialRegime"),
                  hasSpouse,
                }),
            );
            if (!hasMismatch) return true;
            return this.createError({
              message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
            });
          },
        ),
        socialSecurityNumber: noSpaceString
          .typeError(messages.REQUIRED)
          .nullable()
          .optional()
          .test(
            "securityNumberFormat",
            messages.INVALID_VALUE,
            function (value, ctx) {
              if (!value) return true;
              const result = validateNIR(value, this.parent);
              if (!result.valid)
                return ctx.createError({
                  message: messages[result.reason] || messages.INVALID_VALUE,
                });

              return true;
            },
          ),
        socioProfessionalCategory: string,
        politicallyExposedPerson: yup.boolean().nullable(),
        // exerciseFrame: yup.mixed().when("socioProfessionalCategory", {
        //   is: showExerciseFrame,
        //   then: string,
        // }),
        siretNumber: string,
        profession: string,
        acceptContact: yup.boolean().typeError(messages.NOT_BOOLEAN).nullable(),
        acceptDigital: yup.boolean().typeError(messages.NOT_BOOLEAN).nullable(),
        familySituation: string,
        fields: yup.object().shape({
          affiliateOrganizationCode: yup
            .string()
            .typeError(messages.NOT_STRING)
            .nullable(),
        }),
      }),
    }),
    telecoms: yup.object().shape({
      contact: yup.object().shape({
        [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
          value: textField,
        }),
        [CONTACT_POINT_SYSTEM.COMPLEMENT]: yup.object().shape({
          value: textField,
        }),
        [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.BIRTH_CITY]: yup.object().shape({
          value: string.test(
            "lumineisBirthCity",
            messages.REQUIRED,
            function (value) {
              if (!isLumineis) return true;
              const birthCountry = get(
                this.from?.[this.from.length - 1]?.value,
                `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
              );
              if (birthCountry !== "France") return true;
              return Boolean(String(value || "").trim());
            },
          ),
        }),
        [CONTACT_POINT_SYSTEM.BIRTH_ZIP_CODE]: yup.object().shape({
          value: string.test(
            "lumineisBirthZipCode",
            messages.INVALID_VALUE,
            function (value) {
              if (!isLumineis) return true;
              const birthCountry = get(
                this.from?.[this.from.length - 1]?.value,
                `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
              );
              if (birthCountry !== "France") return true;
              return REGEX_EXPS.zipCode.test(String(value || "").trim());
            },
          ),
        }),
        [CONTACT_POINT_SYSTEM.PHONE]: yup.object().shape({
          value: noSpaceString
            .typeError(messages.REQUIRED)
            .nullable()
            .test("isValid", messages.INVALID_VALUE, function (value) {
              if (!value) {
                return true;
              }
              const prefix = getPhonePrefixFromYupContext(this);
              return isValidLocalPhoneNumber(value, prefix);
            }),
          // .test(
          //   "phoneConflict",
          //   messages.PHONE_CONFLICT,
          //   async function (value) {
          //     const isValidRegex = new RegExp(REGEX_EXPS.phone).test(value);
          //     if (!value || !isValidRegex) {
          //       return true;
          //     }
          // const {
          //   data: { user },
          // } = await checkUser({
          //   variables: {
          //     where: { phone: value },
          //   },
          // });
          // return !user?.id;
          // }
          // ),
        }),
        [CONTACT_POINT_SYSTEM.LANDLINE]: yup.object().shape({
          value: noSpaceString
            .typeError(messages.REQUIRED)
            .nullable()
            .test("isValid", messages.INVALID_VALUE, async function (value) {
              return value ? new RegExp(REGEX_EXPS.phone).test(value) : true;
            }),
        }),
        [CONTACT_POINT_SYSTEM.EMAIL]: yup.object().shape({
          value: yup.string().nullable().email(messages.EMAIL),
          // .test(
          //   "emailConflict",
          //   messages.EMAIL_CONFLICT,
          //   async function (value) {
          //     if (!value || !yup.string().email().isValidSync(value)) {
          //       return true;
          //     }
          //     const {
          //       data: { user },
          //     } = await checkUser({
          //       variables: {
          //         where: { email: value },
          //       },
          //     });
          //     return !user?.id;
          //   }
          // ),
        }),
      }),
      payer: yup.object().shape({
        [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
          value: textField,
        }),
        [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
          value: string,
        }),
      }),
      receiver: yup.object().shape({
        // [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
        //   value: string.required(messages.REQUIRED),
        // }),
        // [CONTACT_POINT_SYSTEM.COMPLEMENT]: yup.object().shape({
        //   value: string,
        // }),
        // [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
        //   value: string.required(messages.REQUIRED),
        // }),
        // [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
        //   value: string.required(messages.REQUIRED),
        // }),
      }),
    }),
    spouse:
      hasPartner &&
      relatedPersonValidationSchema({
        birthDate: yup
          .date()
          .typeError(messages.REQUIRED)
          .max(isMajor, messages.RADIANCE.SPOUSE_AGE)
          .min(
            moment().subtract(100, "year").startOf("year"),
            messages.RADIANCE.SPOUSE_AGE,
          )
          .required(messages.REQUIRED),
      }),
    children: yup.array().of(
      yup
        .object()
        .shape({
          authority: yup
            .string()
            .nullable()
            .test("minor-authority", messages.REQUIRED, function (value) {
              const birthdate = this.parent?.identity?.user?.birthDate;
              if (getAgeFromBirthDate(birthdate) > 18) return true;
              return !!value && value.length > 0;
            })
            .test("both-spouse", messages.INVALID_VALUE, function (value) {
              if (value !== AUTHORITY_TYPE.BOTH) return true;
              if (
                get(this.parent, "identity.fields.nirInheritanceFrom") !==
                NIR_INHERITANCE_FROM.SPOUSE
              ) {
                return true;
              }
              // Valid with or without spouse on contract (off-contract NIR on child row).
              return true;
            })
            .test(
              "authority-regime-match",
              messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
              function (value) {
                const child = this.parent;
                const root = this.from[this.from.length - 1].value;
                const hasSpouse = Boolean(get(root, "spouse"));
                if (
                  !childHasSocialRegimeAttachmentMismatch({
                    child: { ...child, authority: value },
                    adherentSocialRegime: get(
                      root,
                      "project.contact.socialRegime",
                    ),
                    spouseSocialRegime: get(
                      root,
                      "spouse.identity.socialRegime",
                    ),
                    hasSpouse,
                  })
                ) {
                  return true;
                }
                return this.createError({
                  message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
                });
              },
            ),
          identity: yup.object().shape({
            user: yup.object().shape({
              lastname: nameValidation,
              firstname: nameValidation,
              birthDate: yup
                .date()
                .typeError(messages.REQUIRED)
                .max(moment(), messages.MIN_TODAY)
                .required(messages.REQUIRED),
            }),
            socioProfessionalCategory: string,
            socialRegime: string,
            socialSecurityNumber: yup
              .string()
              .typeError(messages.REQUIRED)
              .nullable()
              .optional(),
            fields: yup.object().nullable().notRequired(),
          }),
        })
        .test("both-nir-source-fields", messages.REQUIRED, function (child) {
          if (child?.authority !== AUTHORITY_TYPE.BOTH) return true;
          const v = get(child, "identity.fields.nirInheritanceFrom");
          if (
            v === NIR_INHERITANCE_FROM.ADHERENT ||
            v === NIR_INHERITANCE_FROM.SPOUSE
          ) {
            return true;
          }
          return this.createError({
            path: `${this.path}.identity.fields.nirInheritanceFrom`,
            message: messages.REQUIRED,
          });
        })
        .test(
          "child-social-regime-attachment",
          messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
          function (child) {
            const root = this.from[this.from.length - 1].value;
            const hasSpouse = Boolean(get(root, "spouse"));
            if (
              !childHasSocialRegimeAttachmentMismatch({
                child,
                adherentSocialRegime: get(root, "project.contact.socialRegime"),
                spouseSocialRegime: get(root, "spouse.identity.socialRegime"),
                hasSpouse,
              })
            ) {
              return true;
            }
            return this.createError({
              path: `${this.path}.identity.socialRegime`,
              message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
            });
          },
        )
        .test("child-nir-format", messages.INVALID_VALUE, function (child) {
          const value = get(child, "identity.socialSecurityNumber");
          if (!value) return true;
          const authority = child?.authority;
          const result = childUsesStrictNirValidation(authority)
            ? validateNIR(value, child.identity)
            : softValidateNIR(value);
          if (!result.valid) {
            return this.createError({
              path: `${this.path}.identity.socialSecurityNumber`,
              message: messages[result.reason] || messages.INVALID_VALUE,
            });
          }
          return true;
        }),
    ),
    payment: yup.object().shape({
      type: string,
      frequency: string,
      payer: yup.object().shape({
        BIC: noSpaceString
          .typeError(messages.NOT_STRING)
          .nullable()
          .test(
            "validateBIC",
            messages.INVALID_BIC || messages.INVALID_IBAN,
            function (value) {
              if (!value) return true;
              const result = validateBIC(value);
              return result.valid;
            },
          ),
        IBAN: noSpaceString
          .typeError(messages.NOT_STRING)
          .nullable()
          .test("validateIBAN", messages.INVALID_IBAN, function (value) {
            if (!value) return true;
            const result = validateIBAN(value);
            return result.valid;
          }),
        owner: yup.object().shape({
          isAdherent: yup.boolean().typeError(messages.NOT_BOOLEAN),
          firstName: nameValidation,
          lastName: nameValidation,
        }),
      }),
      receiver: yup.object().shape({
        BIC: noSpaceString
          .typeError(messages.NOT_STRING)
          .nullable()
          .test(
            "validateBIC",
            messages.INVALID_BIC || messages.INVALID_IBAN,
            function (value) {
              if (!value) return true;
              const result = validateBIC(value);
              return result.valid;
            },
          ),
        IBAN: noSpaceString
          .typeError(messages.NOT_STRING)
          .nullable()
          .test("validateIBAN", messages.INVALID_IBAN, function (value) {
            if (!value) return true;
            const result = validateIBAN(value);
            return result.valid;
          }),
        owner: yup.object().shape({
          firstName: nameValidation,
          lastName: nameValidation,
        }),
      }),
    }),
  });
};

export default validation;
